import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Button, Form, Table, Alert, Card, Badge } from 'react-bootstrap';
import addIcon from '../assets/icons/add.svg';
import closeIcon from '../assets/icons/close.svg';
import documentIcon from '../assets/icons/document.svg';
import { SearchableCustomerSelect } from './SearchableCustomerSelect';
import { SearchableVehicleSelect } from './SearchableVehicleSelect';
import { CustomerLink, VehicleLink, RTOLink } from './EntityLinks';
import { useFormData } from '../hooks/useFormData';
import { useStores } from '../hooks/useStores';
import { StatusBadge } from './shared/StatusBadge';
import { useURLParams } from '../hooks/useURLParams';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { ColumnConfigDropdown } from './shared/ColumnConfigDropdown';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'customer', label: 'Customer' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'sale_price', label: 'Sale Price' },
  { key: 'balance', label: 'Balance' },
  { key: 'payment', label: 'Payment' },
  { key: 'next_due', label: 'Next Due' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

/**
 * RTOList component displays all RTO agreements and provides creation form
 */
export function RTOList() {
  const navigate = useNavigate();
  const { apiCall, user } = useOutletContext();
  const [rtoAgreements, setRtoAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRTOForm, setShowNewRTOForm] = useState(false);
  const [formError, setFormError] = useState(null);

  // URL-synced filters
  const { getParam, setParam } = useURLParams({ status: 'active' });
  const statusFilter = getParam('status');

  // Column config from URL
  const { visibleColumns, isVisible, toggleColumn, allColumns } = useColumnConfig(COLUMNS);

  // Use stores hook
  const { stores } = useStores(apiCall);

  // Use form data hook
  const getInitialFormData = () => {
    const today = new Date();
    const localDate = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');
    return {
      customer_id: '',
      vehicle_id: '',
      store_id: '',
      total_sale_price: '',
      down_payment: '0',
      recurring_amount: '',
      frequency: 'weekly',
      start_date: localDate,
      notes: '',
    };
  };

  const {
    formData,
    setFormData,
    handleChange: handleInputChange,
    resetForm,
  } = useFormData(getInitialFormData());

  // Load RTO agreements when status filter changes
  useEffect(() => {
    const loadData = async () => {
      try {
        const rtoRes = await apiCall(`${API_BASE_URL}/rto-agreements?status=${statusFilter}`);
        setRtoAgreements(rtoRes?.data || []);
      } catch (error) {
        console.error('Error loading RTO data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [apiCall, statusFilter]);

  const handleAddRTO = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validate required fields
    if (!formData.customer_id) {
      setFormError('Please select a customer.');
      return;
    }
    if (!formData.vehicle_id) {
      setFormError('Please select a vehicle.');
      return;
    }
    if (!formData.store_id) {
      setFormError('Please select a store.');
      return;
    }
    if (!formData.total_sale_price || parseFloat(formData.total_sale_price) <= 0) {
      setFormError('Total sale price must be greater than zero.');
      return;
    }
    if (!formData.recurring_amount || parseFloat(formData.recurring_amount) <= 0) {
      setFormError('Recurring payment amount must be greater than zero.');
      return;
    }

    try {
      const response = await apiCall(
        `${API_BASE_URL}/rto-agreements`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...formData,
            customer_id: parseInt(formData.customer_id),
            vehicle_id: parseInt(formData.vehicle_id),
            store_id: parseInt(formData.store_id),
            total_sale_price: parseFloat(formData.total_sale_price),
            down_payment: parseFloat(formData.down_payment) || 0,
            recurring_amount: parseFloat(formData.recurring_amount),
          }),
        }
      );

      if (response.success || response.data) {
        // Reset form
        setFormData({
          customer_id: '',
          vehicle_id: '',
          store_id: '',
          total_sale_price: '',
          down_payment: '0',
          recurring_amount: '',
          frequency: 'weekly',
          start_date: new Date().toISOString().split('T')[0],
          notes: '',
        });
        setShowNewRTOForm(false);
        alert('Lease agreement created successfully');

        // Refresh list
        const rtoRes = await apiCall(`${API_BASE_URL}/rto-agreements?status=${statusFilter}`);
        setRtoAgreements(rtoRes?.data || []);
      } else {
        setFormError(response.error || 'Failed to create lease agreement');
      }
    } catch (error) {
      setFormError(error.message || 'Error creating lease agreement');
    }
  };

  const formatFrequency = (frequency) => {
    switch (frequency) {
      case 'weekly': return 'Weekly';
      case 'biweekly': return 'Bi-Weekly';
      case 'monthly': return 'Monthly';
      default: return frequency;
    }
  };

  const handleGenerateContract = async (rtoId) => {
    try {
      const response = await apiCall(
        `${API_BASE_URL}/rto-agreements/${rtoId}/generate-contract`,
        { method: 'POST' }
      );

      const base64PDF = response.data.pdfBase64;
      const binaryString = window.atob(base64PDF);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      alert('Failed to generate contract: ' + err.message);
    }
  };

  const renderCell = (key, rto) => {
    switch (key) {
      case 'id':
        return <RTOLink rtoId={rto.id}>#{rto.id}</RTOLink>;
      case 'customer':
        return <CustomerLink customerId={rto.customer_id} customerName={rto.customer_name} />;
      case 'vehicle':
        return (
          <VehicleLink
            vehicleId={rto.vehicle_id}
            vehicleInfo={`${rto.vehicle_year || ''} ${rto.vehicle_make || ''} ${rto.vehicle_model || ''}`}
          >
            {rto.vehicle_make} {rto.vehicle_model}
            <br />
            <small className="text-muted">{rto.vehicle_license_plate}</small>
          </VehicleLink>
        );
      case 'sale_price':
        return `$${parseFloat(rto.total_sale_price).toLocaleString()}`;
      case 'balance':
        return (
          <span style={{ color: parseFloat(rto.balance_remaining) > 0 ? '#d32f2f' : '#388e3c', fontWeight: 'bold' }}>
            ${parseFloat(rto.balance_remaining).toLocaleString()}
          </span>
        );
      case 'payment':
        return `$${parseFloat(rto.recurring_amount).toFixed(0)}/${formatFrequency(rto.frequency).toLowerCase().replace('-', '')}`;
      case 'next_due':
        return rto.next_payment_due_date ? rto.next_payment_due_date.split('T')[0] : '-';
      case 'status':
        return (
          <>
            <StatusBadge status={rto.status} type="rto" delinquencyStatus={rto.delinquency_status} />
            {parseFloat(rto.total_owed) > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#d32f2f' }}>
                Owes: ${parseFloat(rto.total_owed).toFixed(0)}
              </div>
            )}
          </>
        );
      case 'actions':
        return (
          <>
            <Button variant="link" size="sm" onClick={() => navigate(`/dashboard/rto/${rto.id}`)}>
              View
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleGenerateContract(rto.id)}
              title="Generate Agreement PDF"
              className="ms-1"
            >
              <img src={documentIcon} alt="Generate Agreement" style={{ width: 16, height: 16 }} />
            </Button>
          </>
        );
      default: return null;
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div>Loading lease agreements...</div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 domain-transactions">
      <Row className="mb-4">
        <Col>
          <h2>Long Term Lease Agreements</h2>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          {user?.role !== 'auditor' && (
            <Button
              variant={showNewRTOForm ? "secondary" : "primary"}
              onClick={() => setShowNewRTOForm(!showNewRTOForm)}
              className="me-2"
            >
              {showNewRTOForm ? (
                <><img src={closeIcon} alt="" className="icon-btn" />Cancel</>
              ) : (
                <><img src={addIcon} alt="" className="icon-btn" />New Lease Agreement</>
              )}
            </Button>
          )}

          <Form.Select
            style={{ width: 'auto', display: 'inline-block' }}
            value={statusFilter}
            onChange={(e) => setParam('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="defaulted">Defaulted</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <ColumnConfigDropdown allColumns={allColumns} isVisible={isVisible} toggleColumn={toggleColumn} />
        </Col>
      </Row>

      {showNewRTOForm && (
        <Card className="mb-4">
          <Card.Header className="text-white" style={{ backgroundColor: '#3b82f6' }}>
            <h3>New Lease Agreement</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddRTO}>
              {formError && <Alert variant="danger">{formError}</Alert>}

              <Row>
                <Col md={6}>
                  <SearchableCustomerSelect
                    apiCall={apiCall}
                    selectedCustomerId={formData.customer_id}
                    onSelectCustomer={(customerId) => {
                      setFormData({ ...formData, customer_id: customerId });
                    }}
                    required={true}
                  />
                </Col>
                <Col md={6}>
                  <SearchableVehicleSelect
                    apiCall={apiCall}
                    selectedVehicleId={formData.vehicle_id}
                    onSelectVehicle={(vehicleId) => {
                      setFormData({ ...formData, vehicle_id: vehicleId });
                    }}
                    required={true}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Store *</Form.Label>
                    <Form.Select
                      name="store_id"
                      value={formData.store_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a store</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>
                          {store.name} {store.city ? `(${store.city})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Sale Price *</Form.Label>
                    <Form.Control
                      type="number"
                      name="total_sale_price"
                      step="0.01"
                      value={formData.total_sale_price}
                      onChange={handleInputChange}
                      required
                      placeholder="15000.00"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Down Payment</Form.Label>
                    <Form.Control
                      type="number"
                      name="down_payment"
                      step="0.01"
                      value={formData.down_payment}
                      onChange={handleInputChange}
                      placeholder="1000.00"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Recurring Payment *</Form.Label>
                    <Form.Control
                      type="number"
                      name="recurring_amount"
                      step="0.01"
                      value={formData.recurring_amount}
                      onChange={handleInputChange}
                      required
                      placeholder="300.00"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Frequency *</Form.Label>
                    <Form.Select
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleInputChange}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Text className="text-muted">
                      Past dates will generate backfill charges
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Optional notes about this agreement..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" variant="primary">
                Create Lease Agreement
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="p-0">
          <Table striped hover responsive>
            <thead>
              <tr>
                {visibleColumns.map(col => (
                  <th key={col.key} className={col.className}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rtoAgreements.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center py-4">
                    No lease agreements found
                  </td>
                </tr>
              ) : (
                rtoAgreements.map(rto => (
                  <tr key={rto.id}>
                    {visibleColumns.map(col => (
                      <td key={col.key} className={col.className}>{renderCell(col.key, rto)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default RTOList;
